import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Cell
} from 'recharts';
import { AlertTriangle, CheckCircle, Clock, TrendingUp, Download, Layers, FileText, Loader2 } from 'lucide-react';
import { assessments } from '../services/api';

// Domain display names in Portuguese
const domainDisplayNames = {
  'mando': 'Mando',
  'tato': 'Tato',
  'resposta_de_ouvinte': 'Resp. Ouvinte',
  'percepcao_visual_mts': 'Percep. Visual',
  'brincar_independente': 'Brincar Indep.',
  'comportamento_social': 'Comp. Social',
  'imitacao_motora': 'Imitação Motora',
  'ecoico': 'Ecóico',
  'comportamento_verbal_espontaneo': 'Comp. Verbal Esp.',
  'ouvinte_fcc': 'Ouvinte FCC',
  'intraverbal': 'Intraverbal',
  'rotina_sala_aula': 'Rotina Sala Aula',
  'estrutura_linguistica': 'Estrut. Linguística',
  'leitura': 'Leitura',
  'escrita': 'Escrita',
  'matematica': 'Matemática',
};

const levelNames = {
  'level_1': 'Nível I (0-18 meses)',
  'level_2': 'Nível II (18-30 meses)',
  'level_3': 'Nível III (30-48 meses)',
};

const levelColors = {
  'level_1': '#667eea',
  'level_2': '#48bb78',
  'level_3': '#ed8936',
};

const COLORS = {
  excellent: '#48bb78',
  good: '#f6ad55',
  needs_improvement: '#fc8181',
  untested: '#cbd5e0'
};

const getPerformanceColor = (value) => {
  if (value >= 80) return COLORS.excellent;
  if (value >= 50) return COLORS.good;
  if (value > 0) return COLORS.needs_improvement;
  return COLORS.untested;
};

const VBMAPPDashboard = ({ assessmentData }) => {
  const dashboardData = assessmentData?.parsedData || null;
  const loading = false;
  const error = !dashboardData ? 'No assessment processed yet' : null;
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [parentReport, setParentReport] = useState(assessmentData?.latestAssessment?.parentReport || null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState(assessmentData?.latestAssessment?.parentReportGeneratedAt || null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const patientName = assessmentData?.latestAssessment?.patient?.firstName + ' ' + assessmentData?.latestAssessment?.patient?.lastName || 'Patient';
      const date = new Date().toISOString().split('T')[0];
      const filename = `VB-MAPP_Dashboard_${patientName.replace(/\s+/g, '_')}_${date}.pdf`;

      // Fix width so PDF looks the same regardless of screen/window size
      const el = dashboardRef.current;
      const originalWidth = el.style.width;
      el.style.width = '1024px';

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: el.scrollHeight,
        width: 1024
      });

      el.style.width = originalWidth;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const margin = 10;
      const ratio = (pdfWidth - 2 * margin) / imgWidth;
      const scaledWidth = pdfWidth - 2 * margin;
      const scaledHeight = imgHeight * ratio;

      const x = margin;
      const y = 10;

      if (scaledHeight > pdfHeight - 20) {
        let position = 0;
        const pageHeight = pdfHeight - 20;

        while (position < scaledHeight) {
          if (position > 0) {
            pdf.addPage();
          }

          const remainingHeight = scaledHeight - position;
          const currentPageHeight = Math.min(pageHeight, remainingHeight);

          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');

          tempCanvas.width = imgWidth;
          tempCanvas.height = (currentPageHeight / ratio);

          tempCtx.drawImage(canvas,
            0, position / ratio, imgWidth, currentPageHeight / ratio,
            0, 0, imgWidth, currentPageHeight / ratio
          );

          const tempImgData = tempCanvas.toDataURL('image/png');
          pdf.addImage(tempImgData, 'PNG', x, y, scaledWidth, currentPageHeight);

          position += currentPageHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }

      pdf.setFontSize(16);
      pdf.setTextColor(40, 40, 40);
      pdf.text('VB-MAPP Assessment Dashboard', pdfWidth / 2, 8, { align: 'center' });

      const { getVersionString } = await import('../config/version');
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(getVersionString(), pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save(filename);

    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  // Generate parent report
  const generateParentReport = async () => {
    const assessmentId = assessmentData?.latestAssessment?.id;
    if (!assessmentId) return;

    setIsGeneratingReport(true);
    setReportError(null);
    try {
      const response = await assessments.generateParentReport(assessmentId);
      if (response.data.status === 'success') {
        setParentReport(response.data.data.report);
        setReportGeneratedAt(new Date().toISOString());
      } else {
        setReportError(response.data.message || 'Erro ao gerar relatório');
      }
    } catch (err) {
      console.error('Error generating parent report:', err);
      setReportError(err.response?.data?.message || 'Erro ao gerar relatório. Tente novamente.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Simple markdown renderer
  const renderMarkdown = (markdown) => {
    if (!markdown) return null;
    const lines = markdown.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-gray-800 mt-6 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        // Collect consecutive bullet items into a <ul>
        const items = [];
        while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
          items.push(
            <li key={i} className="text-gray-700 mb-1">
              {renderInlineMarkdown(lines[i].replace(/^[-*]\s/, ''))}
            </li>
          );
          i++;
          // Skip blank lines between list items
          while (i < lines.length && lines[i].trim() === '') i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className="ml-4 list-disc mb-2">
            {items}
          </ul>
        );
        continue; // i already advanced
      } else if (/^\d+\.\s/.test(line)) {
        // Collect consecutive numbered items (with description paragraphs) into an <ol>
        const items = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
          const titleLine = lines[i].replace(/^\d+\.\s/, '');
          i++;
          // Skip blank lines after the title
          while (i < lines.length && lines[i].trim() === '') i++;
          // Collect description paragraphs until next numbered item, header, or rule
          const descParts = [];
          while (i < lines.length && !/^\d+\.\s/.test(lines[i]) && !lines[i].startsWith('#') && !lines[i].startsWith('---')) {
            if (lines[i].trim() === '') {
              i++;
              continue;
            }
            descParts.push(
              <p key={`desc-${i}`} className="text-gray-700 mt-1 leading-relaxed">
                {renderInlineMarkdown(lines[i])}
              </p>
            );
            i++;
          }
          items.push(
            <li key={`li-${items.length}`} className="text-gray-700 mb-3">
              {renderInlineMarkdown(titleLine)}
              {descParts}
            </li>
          );
        }
        elements.push(
          <ol key={`ol-${i}`} className="ml-4 list-decimal mb-2">
            {items}
          </ol>
        );
        continue; // i already advanced
      } else if (line.startsWith('---')) {
        elements.push(<hr key={i} className="my-4 border-gray-200" />);
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(
          <p key={i} className="text-gray-700 mb-2 leading-relaxed">
            {renderInlineMarkdown(line)}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  const renderInlineMarkdown = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error || !dashboardData?.VB_MAPP) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <div className="text-yellow-800 font-medium mb-2">Avaliação Não Processada</div>
        <div className="text-yellow-700 text-sm">
          A avaliação precisa ser analisada antes de exibir o dashboard.
        </div>
      </div>
    );
  }

  const vbmapp = dashboardData.VB_MAPP;
  const progress = vbmapp.assessment_progress;
  const levels = vbmapp.levels;
  const domainSummary = vbmapp.domain_summary;

  // Build domain summary data for charts
  const domainChartData = Object.entries(domainSummary).map(([key, data]) => ({
    key,
    name: domainDisplayNames[key] || data.display_name || key,
    shortName: (domainDisplayNames[key] || key).length > 14
      ? (domainDisplayNames[key] || key).substring(0, 11) + '...'
      : (domainDisplayNames[key] || key),
    percentage: data.percentage,
    totalScore: data.total_score,
    maxScore: data.max_score,
    milestonesTested: data.milestones_tested,
    milestonesAchieved: data.milestones_achieved,
  }));

  // Build level-specific data
  const levelSummaryData = Object.entries(levels).map(([levelKey, levelData]) => ({
    key: levelKey,
    name: levelNames[levelKey] || levelKey,
    percentage: levelData.percentage,
    totalScore: levelData.total_score,
    maxScore: levelData.max_score,
    domainCount: Object.keys(levelData.domains).length,
    color: levelColors[levelKey],
  }));

  // Build per-level domain data for selected level view
  const getDomainsForLevel = (levelKey) => {
    const levelData = levels[levelKey];
    if (!levelData) return [];
    return Object.entries(levelData.domains).map(([domainKey, data]) => ({
      key: domainKey,
      name: domainDisplayNames[domainKey] || data.display_name || domainKey,
      shortName: (domainDisplayNames[domainKey] || domainKey).length > 14
        ? (domainDisplayNames[domainKey] || domainKey).substring(0, 11) + '...'
        : (domainDisplayNames[domainKey] || domainKey),
      percentage: data.percentage,
      score: data.score,
      maxScore: data.max_score,
      milestonesTested: data.milestones_tested,
      milestonesAchieved: data.milestones_achieved,
    }));
  };

  const totalAchieved = progress.milestones_achieved;
  const totalTested = progress.milestones_tested;

  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={exportToPDF}
          disabled={isExporting}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
            isExporting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
          }`}
        >
          {isExporting ? (
            <>
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Exportando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </>
          )}
        </button>
      </div>

      {/* Dashboard Content */}
      <div ref={dashboardRef} className="bg-white">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Dashboard VB-MAPP</h2>
              <p className="text-sm text-gray-600 mt-1">
                Verbal Behavior Milestones Assessment and Placement Program
              </p>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p>Data: {new Date().toLocaleDateString('pt-BR')}</p>
              {assessmentData?.latestAssessment?.patient && (
                <p>
                  Paciente: {assessmentData.latestAssessment.patient.firstName} {assessmentData.latestAssessment.patient.lastName}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-brand-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Progresso Geral</div>
                  <div className="text-2xl font-bold text-gray-900">{progress.percent_assessed}%</div>
                  <div className="text-xs text-gray-500">{totalTested} de 170 marcos</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Marcos Alcançados</div>
                  <div className="text-2xl font-bold text-gray-900">{totalAchieved}</div>
                  <div className="text-xs text-gray-500">de {totalTested} avaliados</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-brand-600 to-brand-500 mr-3 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">%</span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Taxa de Alcance</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {totalTested > 0 ? Math.round((totalAchieved / totalTested) * 100) : 0}%
                  </div>
                  <div className="text-xs text-gray-500">Marcos avaliados</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <Layers className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Domínios Avaliados</div>
                  <div className="text-2xl font-bold text-gray-900">{Object.keys(domainSummary).length}</div>
                  <div className="text-xs text-gray-500">de 16 domínios</div>
                </div>
              </div>
            </div>
          </div>

          {/* How to read this report */}
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 text-sm text-gray-700">
            <p className="font-medium text-gray-800 mb-2">Como ler este relatório</p>
            <p className="mb-1">
              O VB-MAPP avalia marcos do <strong>comportamento verbal</strong> em <strong>3 níveis</strong> de desenvolvimento: <strong>Nível I</strong> (0-18 meses — habilidades iniciais de comunicação), <strong>Nível II</strong> (18-30 meses — ampliação de vocabulário e interações) e <strong>Nível III</strong> (30-48 meses — conversas, perguntas e participação em grupo).
            </p>
            <p className="mb-1">
              Os domínios incluem: <strong>Mando</strong> (pedidos), <strong>Tato</strong> (nomeação), <strong>Ouvinte</strong> (compreensão), <strong>Percepção Visual</strong> (pareamento), <strong>Brincar</strong>, <strong>Imitação</strong>, <strong>Ecóico</strong> (repetição), <strong>Intraverbal</strong> (conversação), <strong>LRFFC</strong> (funções e categorias), <strong>Rotina de Grupo</strong> e <strong>Estrutura Linguística</strong>.
            </p>
            <p>
              Nos gráficos: <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#48bb78'}}></span> <strong>Verde</strong> (≥80%) = excelente &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#f6ad55'}}></span> <strong>Laranja</strong> (50–79%) = bom &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#fc8181'}}></span> <strong>Vermelho</strong> (&lt;50%) = precisa melhorar
            </p>
          </div>

          {/* Level Progress Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {levelSummaryData.map((level) => (
              <div key={level.key} className="bg-white rounded-lg shadow-sm p-6 border-l-4" style={{ borderLeftColor: level.color }}>
                <h4 className="text-sm font-medium text-gray-600 mb-2">{level.name}</h4>
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold" style={{ color: level.color }}>{level.percentage}%</span>
                  <span className="text-sm text-gray-500">
                    ({level.totalScore}/{level.maxScore} pontos)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
                  <div
                    className="h-3 rounded-full transition-all duration-500"
                    style={{ width: `${level.percentage}%`, backgroundColor: level.color }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{level.domainCount} domínios avaliados</div>
              </div>
            ))}
          </div>

          {/* Level Filter */}
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedLevel('all')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedLevel === 'all'
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todos os Níveis
            </button>
            {Object.entries(levelNames).map(([key, name]) => (
              <button
                key={key}
                onClick={() => setSelectedLevel(key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  selectedLevel === key
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={selectedLevel === key ? { backgroundColor: levelColors[key] } : {}}
              >
                {name.split(' (')[0]}
              </button>
            ))}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Domain Performance Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedLevel === 'all' ? 'Desempenho por Domínio (Geral)' : `Desempenho - ${levelNames[selectedLevel]}`}
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={selectedLevel === 'all' ? domainChartData : getDomainsForLevel(selectedLevel)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="shortName"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value}%`,
                        'Desempenho'
                      ]}
                      labelFormatter={(label) => {
                        const item = (selectedLevel === 'all' ? domainChartData : getDomainsForLevel(selectedLevel))
                          .find(d => d.shortName === label);
                        return item?.name || label;
                      }}
                      contentStyle={{ backgroundColor: '#f7fafc', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="percentage" name="Desempenho (%)">
                      {(selectedLevel === 'all' ? domainChartData : getDomainsForLevel(selectedLevel)).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getPerformanceColor(entry.percentage)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Radar Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Perfil de Operantes Verbais</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={selectedLevel === 'all' ? domainChartData : getDomainsForLevel(selectedLevel)}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="shortName" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Radar
                      name="Desempenho"
                      dataKey="percentage"
                      stroke={selectedLevel !== 'all' ? levelColors[selectedLevel] : '#667eea'}
                      fill={selectedLevel !== 'all' ? levelColors[selectedLevel] : '#667eea'}
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                    <Tooltip formatter={(value) => [`${value}%`, 'Desempenho']} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Stacked Level Comparison Chart */}
          {selectedLevel === 'all' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Comparação por Nível e Domínio</h3>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={domainChartData.map(d => {
                      const result = { name: d.shortName, fullName: d.name };
                      for (const [levelKey, levelData] of Object.entries(levels)) {
                        const domainData = levelData.domains[d.key];
                        result[levelKey] = domainData ? domainData.percentage : 0;
                      }
                      return result;
                    })}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip
                      formatter={(value, name) => [`${value}%`, levelNames[name] || name]}
                      contentStyle={{ backgroundColor: '#f7fafc', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="level_1" fill={levelColors.level_1} name="level_1" />
                    <Bar dataKey="level_2" fill={levelColors.level_2} name="level_2" />
                    <Bar dataKey="level_3" fill={levelColors.level_3} name="level_3" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Level Legend */}
              <div className="flex justify-center gap-6 mt-4 text-sm">
                {Object.entries(levelNames).map(([key, name]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: levelColors[key] }}></div>
                    <span>{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Domain Status Grid */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Status de Todos os Domínios VB-MAPP</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {domainChartData.map((domain) => (
                <div key={domain.key} className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xs font-bold mx-auto mb-2"
                    style={{ backgroundColor: getPerformanceColor(domain.percentage) }}
                    title={domain.name}
                  >
                    {domain.percentage}%
                  </div>
                  <div className="text-xs text-gray-600 mb-1 leading-tight">{domain.name}</div>
                  <div className="text-xs text-gray-500">{domain.milestonesAchieved}/{domain.milestonesTested}</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${domain.percentage}%`,
                        backgroundColor: getPerformanceColor(domain.percentage)
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.excellent }}></div>
                <span>Excelente (80%+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.good }}></div>
                <span>Bom (50-79%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.needs_improvement }}></div>
                <span>Precisa Melhorar (&lt;50%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.untested }}></div>
                <span>Sem Dados</span>
              </div>
            </div>
          </div>

          {/* Parent Report Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Relatório para Família</h3>
                <p className="text-sm text-gray-500">
                  Relatório em linguagem acessível para compartilhar com os pais
                  {reportGeneratedAt && (
                    <span className="ml-2 text-gray-400">
                      — Gerado em {new Date(reportGeneratedAt).toLocaleDateString('pt-BR')} às {new Date(reportGeneratedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={generateParentReport}
                disabled={isGeneratingReport}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isGeneratingReport
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
                }`}
              >
                {isGeneratingReport ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {parentReport ? 'Gerar Novamente' : 'Gerar Relatório'}
                  </>
                )}
              </button>
            </div>

            {reportError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{reportError}</p>
              </div>
            )}

            {parentReport && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 prose prose-sm max-w-none">
                {renderMarkdown(parentReport)}
              </div>
            )}

            {!parentReport && !reportError && !isGeneratingReport && (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Clique em "Gerar Relatório" para criar um relatório em linguagem acessível para os pais.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VBMAPPDashboard;
